import { Fetch, FetchOptions } from '@/lib/fetch';
import {
  ChangeEmailVerifyDto,
  CurrentUserDto,
  EmailVerificationCodeCreateDto,
  EmailVerifyDto,
  FindEmailPasswordDto,
  FindPasswordDto,
  FindUserEmailDto,
  ImageVerificationCodeDto,
  LoginV2Dto,
  MarketingInquiryDto,
  MessageVerificationCodeCreateDto,
  SysOpsSimpleInfoDto,
  UserCreateMobileDto,
  UserCreatePwdDto,
  UserDto,
  UserLoginDto,
  UserLoginResultDto,
  UserRegisterDto,
  UserRegisteredStatusDto,
  UserRegisteredVerifyDto,
  UserResetPwdDto,
  UserSendActivationEmailDto,
  UserSetEmailDto,
  UserUpdateSelfDto,
  UserVerifyPwdDto,
  VerifyCodeDto,
  VerifyPasswordDto,
} from '@wanjia/panama-sdk';
export const userService = {
  /**
   * 用户登录-/api/user/login/v1
   **/
  async login<T = UserLoginResultDto>(data: UserLoginDto, options?: FetchOptions): Promise<T> {
    return Fetch.post(`/api/user/login/v1`, {
      data,
      ...options,
    });
  },
  /**
   * 用户注册-/api/auth/register/v1
   **/
  async register<T = UserDto>(data: UserRegisterDto, options?: FetchOptions): Promise<T> {
    return Fetch.post(`/api/auth/register/v1`, {
      data,
      ...options,
    });
  },
  /**
   * 营销调查-/api/auth/marketing/v1
   **/
  async marketingInquiry<T = object>(data: MarketingInquiryDto, options?: FetchOptions): Promise<T> {
    return Fetch.post(`/api/auth/marketing/v1`, {
      data,
      ...options,
    });
  },
  /**
   * 找回密码-/api/auth/changePwd/v1
   **/
  async changePassword<T = object>(data: FindPasswordDto, options?: FetchOptions): Promise<T> {
    return Fetch.post(`/api/auth/changePwd/v1`, {
      data,
      ...options,
    });
  },
  /**
   * 找回密码(邮箱)-/api/auth/sendEmail/v1
   **/
  async sendEmailWithPasswordResetLink<T = object>(data: FindEmailPasswordDto, options?: FetchOptions): Promise<T> {
    return Fetch.post(`/api/auth/sendEmail/v1`, {
      data,
      ...options,
    });
  },
  /**
   * 发送手机验证码-/api/verificationCodes/message/v1
   **/
  async messageVerificationCode<T = object>(
    data: MessageVerificationCodeCreateDto,
    options?: FetchOptions,
  ): Promise<T> {
    return Fetch.post(`/api/verificationCodes/message/v1`, {
      data,
      ...options,
    });
  },
  /**
   * 图形验证码-/api/verificationCodes/image/v1
   **/
  async imageVerificationCode<T = ImageVerificationCodeDto>(options?: FetchOptions): Promise<T> {
    return Fetch.get(`/api/verificationCodes/image/v1`, {
      ...options,
    });
  },
  /**
   * 邮箱验证-/api/verifyEmail/v1
   **/
  async verifyEmail<T = object>(data: EmailVerifyDto, options?: FetchOptions): Promise<T> {
    return Fetch.post(`/api/verifyEmail/v1`, {
      data,
      ...options,
    });
  },
  /**
   * 该帐号是否已注册-/api/auth/registeredVerification/v1
   **/
  async registeredVerification<T = UserRegisteredStatusDto>(
    data: UserRegisteredVerifyDto,
    options?: FetchOptions,
  ): Promise<T> {
    return Fetch.post(`/api/auth/registeredVerification/v1`, {
      data,
      ...options,
    });
  },
  /**
   * 验证用户密码-/api/auth/verifyPassword/v1
   **/
  async verifyPassword<T = object>(data: VerifyPasswordDto, options?: FetchOptions): Promise<T> {
    return Fetch.post(`/api/auth/verifyPassword/v1`, {
      data,
      ...options,
    });
  },
  /**
   * 查询用户邮箱-/api/userEmail/v1
   **/
  async findUserIdByUserEmail<T = object>(data: FindUserEmailDto, options?: FetchOptions): Promise<T> {
    return Fetch.post(`/api/userEmail/v1`, {
      data,
      ...options,
    });
  },
  /**
   * 手机号登录-/api/auth/login/v2
   **/
  async mobileLogin<T = UserLoginResultDto>(data: LoginV2Dto, options?: FetchOptions): Promise<T> {
    return Fetch.post(`/api/auth/login/v2`, {
      data,
      ...options,
    });
  },
  /**
   * 重置密码-/api/auth/restPwd/v1
   **/
  async resetPassword<T = object>(data: UserResetPwdDto, options?: FetchOptions): Promise<T> {
    return Fetch.put(`/api/auth/restPwd/v1`, {
      data,
      ...options,
    });
  },
  /**
   * 登录并获取企业列表-/api/user/companies/v1
   **/
  async loginAndGetCompanies<T = { token: string; companies: { id: string; name: string }[] }>(
    data: UserLoginDto,
    options?: FetchOptions,
  ): Promise<T> {
    return Fetch.post(`/api/user/companies/v1`, {
      data,
      ...options,
    });
  },
  async currentUser<T = CurrentUserDto>(options?: FetchOptions): Promise<T> {
    return Fetch.get(`/api/sysOps/user/current/v1`, {
      ...options,
    });
  },
  /**
   * 获取当前用户所属企业列表（登录流程用，无需 sysOps 权限）-/api/user/current/v1
   **/
  async currentUserCompanies<T = CurrentUserDto>(options?: FetchOptions): Promise<T> {
    return Fetch.get(`/api/user/current/v1`, {
      ...options,
    });
  },
  /**
   * 用户查看运营基础信息-/api/user/simpleInfo/{userId}/v1
   **/
  async findUserSimpleInfo<T = SysOpsSimpleInfoDto>(userId: string, options?: FetchOptions): Promise<T> {
    return Fetch.get(`/api/user/simpleInfo/${userId}/v1`, {
      ...options,
    });
  },
  /**
   * 用户信息-/api/user/v1
   **/
  async userUpdateUserInfo<T = UserDto>(data: UserUpdateSelfDto, options?: FetchOptions): Promise<T> {
    return Fetch.put(`/api/user/v1`, {
      data,
      ...options,
    });
  },
  /**
   * 设置手机号-/api/user/setMobile/v1
   **/
  async userSetMobile<T = object>(data: UserCreateMobileDto, options?: FetchOptions): Promise<T> {
    return Fetch.put(`/api/user/setMobile/v1`, {
      data,
      ...options,
    });
  },
  /**
   * 验证密码-/api/auth/verifyPwd/v1
   **/
  async userVerifyPwd<T = object>(data: UserVerifyPwdDto, options?: FetchOptions): Promise<T> {
    return Fetch.put(`/api/auth/verifyPwd/v1`, {
      data,
      ...options,
    });
  },
  /**
   * /api/auth/newToken/v1
   **/
  async newToken<T = UserLoginResultDto>(options?: FetchOptions): Promise<T> {
    return Fetch.post(`/api/auth/newToken/v1`, {
      ...options,
    });
  },
  /**
   * 发送邮箱注册邮件-/api/auth/v1/sendActivationEmail
   **/
  async sendActivationEmail<T = object>(data: UserSendActivationEmailDto, options?: FetchOptions): Promise<T> {
    return Fetch.post(`/api/auth/v1/sendActivationEmail`, {
      data,
      ...options,
    });
  },
  /**
   * 发送邮件登录code邮件-/api/auth/v1/emailLoginCode
   **/
  async sendEmailLoginCode<T = object>(data: EmailVerificationCodeCreateDto, options?: FetchOptions): Promise<T> {
    return Fetch.post(`/api/auth/v1/emailLoginCode`, {
      data,
      ...options,
    });
  },
  /**
   * 查询用户是否已设置密码-/api/auth/v1/isHasPassword
   **/
  async findUserPassWord<T = object>(options?: FetchOptions): Promise<T> {
    return Fetch.post(`/api/auth/v1/isHasPassword`, {
      ...options,
    });
  },
  /**
   * 设置密码-/api/auth/v1/userSetPassword
   **/
  async userSetPassword<T = object>(data: UserCreatePwdDto, options?: FetchOptions): Promise<T> {
    return Fetch.post(`/api/auth/v1/userSetPassword`, {
      data,
      ...options,
    });
  },
  /**
   * 设置邮箱-/api/auth/v1/setEmail
   **/
  async userSetEmail<T = object>(data: UserSetEmailDto, options?: FetchOptions): Promise<T> {
    return Fetch.post(`/api/auth/v1/setEmail`, {
      data,
      ...options,
    });
  },
  /**
   * 变更邮箱验证-/api/auth/verifyChangeEmail/v1
   **/
  async verifyChangeEmail<T = object>(data: ChangeEmailVerifyDto, options?: FetchOptions): Promise<T> {
    return Fetch.put(`/api/auth/verifyChangeEmail/v1`, {
      data,
      ...options,
    });
  },
  /**
   * 校验验证码-/api/auth/verifyCode/v1
   **/
  async verifyCode<T = object>(data: VerifyCodeDto, options?: FetchOptions): Promise<T> {
    return Fetch.post(`/api/auth/verifyCode/v1`, {
      data,
      ...options,
    });
  },
};
